import type { FieldPath, FieldValues } from 'react-hook-form'
import Row from '@app/components/layout/Row'
import { TextInput } from '../../input/text/TextInput'
import { FormField } from '../FormField'
import { useFieldRequired } from '../formSchemaContext'

export type DateTimeSplitFieldProps<T extends FieldValues = FieldValues> = {
  name: FieldPath<T>
  dateLabel?: string
  datePlaceholder?: string
  timeLabel?: string
  timePlaceholder?: string
  timeStep?: number
  required?: boolean
}

/**
 * Presents a single `YYYY-MM-DDTHH:MM` datetime form value as two inputs — a
 * date and a time picker — while keeping the underlying value a single string.
 * The Controller value is the source of truth: it's split for display and
 * recombined on change, so no local state or effects are needed.
 */
export const DateTimeSplitField = <T extends FieldValues = FieldValues>({
  name,
  dateLabel,
  datePlaceholder,
  timeLabel,
  timePlaceholder,
  timeStep,
  required,
}: DateTimeSplitFieldProps<T>) => {
  const fieldRequired = useFieldRequired(name)
  const isRequired = required ?? fieldRequired

  return (
    <FormField<T>
      name={name}
      render={({ value, onChange, onBlur, errorMessage }) => {
        const [date = '', time = ''] = ((value as string) ?? '').split('T')
        const combine = (d: string, tm: string) => (d && tm ? `${d}T${tm}` : d)

        return (
          <Row gap="1rem" style={{ alignItems: 'stretch' }}>
            <TextInput
              label={dateLabel}
              placeholder={datePlaceholder}
              inputType="date"
              required={isRequired}
              text={date}
              setText={(d) => onChange(combine(d, time))}
              onBlur={onBlur}
              errorMessage={errorMessage}
              style={{ flex: 1 }}
            />
            <TextInput
              label={timeLabel}
              placeholder={timePlaceholder}
              inputType="time"
              required={isRequired}
              text={time.substring(0, 5)}
              setText={(tm) => onChange(combine(date, tm))}
              onBlur={onBlur}
              style={{ flex: 1 }}
              step={timeStep}
            />
          </Row>
        )
      }}
    />
  )
}
